import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackOrderPage = () => {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) {
      setError("Please enter both order ID and email");
      return;
    }

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const response = await axios.post(`${API}/orders/track`, {
        order_id: orderId.trim(),
        email: email.trim().toLowerCase()
      });
      setOrder(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Order not found. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered": return <CheckCircle className="w-6 h-6 text-green-400" />;
      case "shipped": return <Truck className="w-6 h-6 text-purple-400" />;
      case "processing": return <Package className="w-6 h-6 text-blue-400" />;
      case "cancelled": return <AlertCircle className="w-6 h-6 text-red-400" />;
      default: return <Clock className="w-6 h-6 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      processing: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      shipped: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      delivered: "bg-green-500/20 text-green-400 border-green-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return colors[status] || colors.pending;
  };

  const statusSteps = ["pending", "processing", "shipped", "delivered"];

  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
            Track Your Order
          </h1>
          <p className="text-[#F4F1DE]/60">
            Enter your order ID and email to check the status of your enclosure
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleTrack} className="bg-[#121212] border border-white/10 p-6 md:p-8 mb-8">
          <div className="space-y-4">
            <div>
              <Label htmlFor="orderId" className="text-[#F4F1DE]/80">Order ID</Label>
              <Input
                id="orderId"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value.toUpperCase())}
                placeholder="e.g., A1B2C3D4"
                className="mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12 font-mono"
                data-testid="track-order-id"
              />
              <p className="text-[#F4F1DE]/40 text-xs mt-1">First 8 characters from your confirmation email</p>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#F4F1DE]/80">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-2 bg-[#0A0A0A] border-white/10 text-[#F4F1DE] h-12"
                data-testid="track-email"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 font-bold"
              data-testid="track-submit"
            >
              {loading ? (
                <span className="spinner" />
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Track Order
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Order Result */}
        {order && (
          <div className="bg-[#121212] border border-white/10 p-6 md:p-8 space-y-6" data-testid="track-result">
            {/* Order Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#F4F1DE]">
                  Order #{order.id.slice(0, 8).toUpperCase()}
                </h2>
                <p className="text-[#F4F1DE]/60 text-sm">
                  Placed on {new Date(order.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <span className={`px-4 py-2 rounded-full border ${getStatusColor(order.status)}`}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>

            {/* Progress Steps */}
            <div className="relative">
              <div className="flex justify-between mb-2">
                {statusSteps.map((step, index) => {
                  const currentIndex = statusSteps.indexOf(order.status);
                  const isActive = index <= currentIndex;
                  const isCurrent = step === order.status;
                  
                  return (
                    <div key={step} className="flex flex-col items-center flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        isActive 
                          ? 'bg-[#E07A5F] border-[#E07A5F] text-white' 
                          : 'bg-[#0A0A0A] border-white/20 text-white/40'
                      } ${isCurrent ? 'ring-4 ring-[#E07A5F]/30' : ''}`}>
                        {index + 1}
                      </div>
                      <span className={`mt-2 text-xs ${isActive ? 'text-[#F4F1DE]' : 'text-[#F4F1DE]/40'}`}>
                        {step.charAt(0).toUpperCase() + step.slice(1)}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Progress Line */}
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-white/10 -z-10 mx-12">
                <div 
                  className="h-full bg-[#E07A5F] transition-all duration-500"
                  style={{ width: `${(statusSteps.indexOf(order.status) / (statusSteps.length - 1)) * 100}%` }}
                />
              </div>
            </div>

            {/* Tracking Info */}
            {order.tracking_number && (
              <div className="bg-[#0A0A0A] border border-white/5 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Truck className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-[#F4F1DE]">Tracking Information</span>
                </div>
                <p className="text-[#F4F1DE]/80 font-mono text-lg">{order.tracking_number}</p>
                {order.courier && (
                  <p className="text-[#F4F1DE]/60 text-sm mt-1">via {order.courier}</p>
                )}
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-[#E07A5F] hover:underline"
                  >
                    Track with {order.courier}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {/* Order Items */}
            <div>
              <h3 className="font-medium text-[#F4F1DE] mb-3">Order Items</h3>
              <div className="space-y-2">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-[#F4F1DE]/80 bg-[#0A0A0A] p-3 rounded">
                    <span>
                      {item.config?.width}×{item.config?.height}×{item.config?.depth}ft {item.config?.shape}
                    </span>
                    <span>×{item.quantity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Address */}
            {order.shipping_address && (
              <div>
                <h3 className="font-medium text-[#F4F1DE] mb-2">Delivery Address</h3>
                <p className="text-[#F4F1DE]/60 text-sm">{order.shipping_address}</p>
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between items-center pt-4 border-t border-white/10">
              <span className="text-[#F4F1DE]/60">Total Paid</span>
              <span className="text-2xl font-bold text-[#E07A5F]">£{order.total?.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="text-center mt-8">
          <p className="text-[#F4F1DE]/40 text-sm">
            Can't find your order? Contact us at{" "}
            <a href="mailto:repticraftstore@gmail.com" className="text-[#E07A5F] hover:underline">
              repticraftstore@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrackOrderPage;
